import styled, {css, keyframes} from 'styled-components';

import {mixins} from '../../styles';

export type TSkeletonItemProps = {
  width?: string;
  height?: string;
};

export type TSkeletonDoughnutProps = {
  size: string;
  cutoutPercentage: number;
};

const waveKeyframe = keyframes`
  0% {
    transform: translateX(-100%);
  }
  50% {
    /* +0.5s of delay between each loop */
    transform: translateX(100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const getSkeletonDoughnutStyle = ({
  size,
  cutoutPercentage,
  theme,
}: TTheme & TSkeletonDoughnutProps) => css`
  ${mixins.round(size)}
  border: solid calc(${size} * ${100 - cutoutPercentage} / 200) ${theme.palette.grey50};
`;

const getSkeletonDefaultStyle = css`
  position: relative;
  overflow: hidden;
  ${({theme}) => css`
    background-color: ${theme.palette.grey50};
    border-radius: ${theme.borderRadius.br1};
    &::after {
      animation: ${waveKeyframe} 1.6s linear 0.5s infinite;
      background: linear-gradient(90deg, transparent, ${theme.palette.grey300}, transparent);
      content: '';
      position: absolute;
      transform: translateX(-100%); /* Avoid flash during server-side hydration */
      inset: 0;
    }
  `};
`;

const SkeletonItem = styled.div<TSkeletonItemProps>`
  ${({width = '100%', height = '20px'}) => mixins.wh(width, height)}
  ${getSkeletonDefaultStyle}
`;

const SkeletonDoughnut = styled.div<TSkeletonDoughnutProps>`
  ${getSkeletonDefaultStyle}
  ${getSkeletonDoughnutStyle}
  border-radius: 50%;
  background: none;
`;

export type TSkeletonCircleProps = {
  size: string;
};

const SkeletonCircle = styled.div<TSkeletonCircleProps>`
  ${getSkeletonDefaultStyle}
  ${({size}) => mixins.round(size)}
`;

export {SkeletonCircle, SkeletonItem, SkeletonDoughnut};
